import type { DepartmentTreeNode } from "@/types/department";

export interface DepartmentTreeFilters {
  search?: string;
  requiresApproval?: boolean;
  isActive?: boolean;
}

export interface DepartmentStatsSummary {
  total: number;
  requiresApproval: number;
  withSubAdmin: number;
  withoutSubAdmin: number;
}

/** Depth-first flatten of the department tree. */
export function flattenDepartmentTree(
  nodes: DepartmentTreeNode[],
): DepartmentTreeNode[] {
  return nodes.flatMap((node) => [
    node,
    ...flattenDepartmentTree(node.children ?? []),
  ]);
}

function nodeMatchesFilters(
  node: DepartmentTreeNode,
  filters: DepartmentTreeFilters,
): boolean {
  const query = (filters.search ?? "").trim().toLowerCase();
  if (query) {
    const matchesSearch =
      node.nameAr.toLowerCase().includes(query) ||
      (node.name?.toLowerCase().includes(query) ?? false) ||
      (node.code?.toLowerCase().includes(query) ?? false);
    if (!matchesSearch) return false;
  }
  if (
    filters.requiresApproval !== undefined &&
    node.requiresApproval !== filters.requiresApproval
  ) {
    return false;
  }
  if (filters.isActive !== undefined && node.isActive !== filters.isActive) {
    return false;
  }
  return true;
}

export function hasActiveDepartmentFilters(
  filters: DepartmentTreeFilters,
): boolean {
  return Boolean(
    (filters.search ?? "").trim() ||
      filters.requiresApproval !== undefined ||
      filters.isActive !== undefined,
  );
}

/**
 * Prunes the tree to nodes that match all active filters or have a matching
 * descendant, keeping the ancestor chain of every match visible.
 */
export function filterDepartmentTree(
  nodes: DepartmentTreeNode[],
  filters: DepartmentTreeFilters,
): DepartmentTreeNode[] {
  if (!hasActiveDepartmentFilters(filters)) return nodes;

  const result: DepartmentTreeNode[] = [];
  for (const node of nodes) {
    const filteredChildren = filterDepartmentTree(node.children ?? [], filters);
    if (nodeMatchesFilters(node, filters) || filteredChildren.length > 0) {
      result.push({ ...node, children: filteredChildren });
    }
  }
  return result;
}

export function computeDepartmentStats(
  nodes: DepartmentTreeNode[],
): DepartmentStatsSummary {
  const flat = flattenDepartmentTree(nodes);
  return {
    total: flat.length,
    requiresApproval: flat.filter((d) => d.requiresApproval).length,
    withSubAdmin: flat.filter((d) => d.subAdminId).length,
    withoutSubAdmin: flat.filter((d) => !d.subAdminId).length,
  };
}
